import { t } from "@/i18n-js/instance";
import type { ConnectionInitiator } from "@/react/api/admin/connections";
import { notificationActionType } from "@/react/components/Notifications/NotificationsList/List/Actions/ActionContent";
import { ConnectionNote } from "@/react/components/ProfileModalV3/Body/ConnectBanner/ConnectionNote";
import { useAcceptConnectionForUser } from "@/react/components/ProfileModalV3/Body/ConnectBanner/useAcceptConnectionForUser";
import { useIgnoreConnectionForUser } from "@/react/components/ProfileModalV3/Body/ConnectBanner/useIgnoreConnectionForUser";
import { Button } from "@/react/components/shared/uikit/Button";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { Notification } from "@/react/types/Notification";
import { GroupUserImage } from "../GroupUserImage";
import { Item } from "../Item";
import { Actions } from "../Item/Actions";
import { TimeStamp } from "../TimeStamp";
import { UserImageWithIcon } from "../UserImageWithIcon";

const i18nBase = "member_profile_modal.notifications";

export const ConnectRequestNotification = ({
  notification,
  isMarkedAsRead = false,
}: {
  notification: Notification;
  isMarkedAsRead?: boolean;
}) => {
  const { acceptConnectionForUser, isAccepting } = useAcceptConnectionForUser({
    user: notification.notifiable.initiator as ConnectionInitiator,
  });

  const { ignoreConnectionForUser, isIgnoring } = useIgnoreConnectionForUser({
    memberPublicUid: notification.notifiable_id.toString(),
  });

  const handleAcceptConnection = () => {
    acceptConnectionForUser(notification.notifiable_id.toString());
  };

  const handleIgnoreConnection = () => {
    ignoreConnectionForUser(notification.notifiable_id.toString());
  };

  return (
    <div
      className={classNames(
        "flex w-full items-center",
        notificationActionType(notification),
      )}
    >
      {notification.second_actor_name ? (
        <GroupUserImage notification={notification} />
      ) : (
        <UserImageWithIcon
          src={notification.actor_image || ""}
          name={notification.actor_name}
          action={notification.action}
          size="9"
        />
      )}
      <div
        className="flex w-full flex-col items-start space-y-1"
        data-testid="notification-details"
      >
        <Item notification={notification} />
        <TimeStamp createdAt={notification.created_at} />
        {notification.notifiable.note && (
          <ConnectionNote
            note={notification.notifiable.note as string}
            collapse
          />
        )}
        <div className="flex gap-2">
          <Button
            variant="primary"
            type="button"
            onClick={handleAcceptConnection}
            disabled={isAccepting}
          >
            {t(`${i18nBase}.accept`)}
          </Button>
          <Button
            variant="secondary"
            type="button"
            onClick={handleIgnoreConnection}
            disabled={isIgnoring}
          >
            {t(`${i18nBase}.ignore`)}
          </Button>
        </div>
      </div>

      <div>
        {!notification.read_at && !isMarkedAsRead ? (
          <div className="flex size-6 items-center justify-center group-hover:hidden">
            <span className="bg-messaging-info-blue inline-block size-2 rounded-full group-hover:hidden" />
          </div>
        ) : (
          <div className="flex size-6 group-hover:hidden" />
        )}
        <Actions notification={notification} />
      </div>
    </div>
  );
};
